import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import { IProductSchema } from '../../../../../graphql/type/product';
import { useDisallowDropshippingProductsMutation } from '../../../../../graphql/mutation/product/disallowDropshippingProductsMutation';
import useNotification from '../../../../components/_hook/useNotification';
import DialogConfirm from '../../../../components/_dialog/DialogConfirm';
import BaseButton from '../../../../components/_button/_module/BaseButton';
import BaseIconButton from '../../../../components/_iconButton/_module/BaseIconButton';

interface IProps {
  products: IProductSchema[];
  onCompleted?: () => void;
  component?: (toggleRemoveDialog: () => void) => ReactElement;
  iconButton?: boolean;
}

export default function ButtonDisallowDropshipping(props: IProps) {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { onCompleted, component, products, iconButton } = props;
  const [isOpenRemoveDialog, setIsOpenRemoveDialog] = useState(false);

  const [
    disallowDropshippingProductsMutation,
    { loading: isDisallowingDropshippingProducts },
  ] = useDisallowDropshippingProductsMutation(
    `updatedProducts {
				_id
				dropshipping {
				  enable
				}
			}`,
    {
      onCompleted: (data) => {
        if (
          data &&
          data.disallowDropshippingProducts &&
          data.disallowDropshippingProducts.updatedProducts
        ) {
          const count =
            data.disallowDropshippingProducts.updatedProducts.length;
          notification.success(
            `${t('__item___updated', {
              item: `${count} ${t('product', {
                count,
              })}`,
            })}`
          );
        }
        if (onCompleted) {
          onCompleted();
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  function toggleRemoveDialog() {
    setIsOpenRemoveDialog(!isOpenRemoveDialog);
  }

  async function onClickDisallowDropshipping() {
    if (products.length) {
      await disallowDropshippingProductsMutation({
        variables: {
          _ids: products.map((product) => product._id),
        },
      });
    }
  }

  return (
    <>
      <DialogConfirm
        title={t(
          'are_you_sure_you_want_to_disallow_dropshipping_disallow_dropshipping_will_remove_all_current_dropshipping_product_from_dropshipper_and_related_dropshipping_request'
        )}
        open={isOpenRemoveDialog}
        onClose={toggleRemoveDialog}
        onConfirm={onClickDisallowDropshipping}
      />
      {iconButton ? (
        <BaseIconButton
          onClick={toggleRemoveDialog}
          tooltip={t('allow_dropshipping')}
          loading={isDisallowingDropshippingProducts}
          size="small"
        >
          <MoveToInboxIcon htmlColor="#F92949" fontSize="small" />
        </BaseIconButton>
      ) : (
        <>
          {component ? (
            component(toggleRemoveDialog)
          ) : (
            <BaseButton
              color="primary"
              variant="contained"
              size="small"
              tooltip={t('disallow_dropshipping')}
              onClick={toggleRemoveDialog}
              style={{ color: '#F92949' }}
              startIcon={<MoveToInboxIcon fontSize="small" />}
            >
              {t('disallow_dropshipping')}
            </BaseButton>
          )}
        </>
      )}
    </>
  );
}
